import { BRAND_SITE_NAME, BRAND_PERSON_NAME, BRAND_HANDLE, BRAND_SITE_URL } from "@/lib/brandConfig";
export type Language = string; // Now supports regional codes like "es-AR", "en-US", etc.

// Extract base language code from regional code (e.g., "es-AR" -> "es")
export const getBaseLanguage = (regionalCode: string | undefined): "es" | "en" => {
  if (!regionalCode) return "es"; // Default to Spanish
  const baseCode = regionalCode.split("-")[0].toLowerCase();
  return baseCode === "en" ? "en" : "es";
};

export const getBrowserLanguage = (region?: "LATAM" | "US" | "OTHER"): Language => {
  if (typeof window === "undefined") return "es-US";
  
  // Get browser language with regional code
  const browserLang = navigator.language || (navigator as unknown as { userLanguage?: string }).userLanguage;
  
  // If region is LATAM, use Spanish with detected country
  if (region === "LATAM") {
    // Try to get country from browser language
    const parts = browserLang.split("-");
    if (parts[0].toLowerCase() === "es" && parts[1]) {
      return browserLang; // Keep the regional code like "es-AR", "es-MX"
    }
    return "es-US"; // Default Spanish
  }
  
  // If region is US, use English US
  if (region === "US") {
    return "en-US";
  }
  
  // For OTHER or no region, return the browser's language code
  return browserLang || "en-US";
};

export const translations = {
  es: {
    common: {
      siteName: BRAND_SITE_NAME,
      tagline: "Modelo de Buenos Aires · redes y crecimiento",
      description:
        `${BRAND_PERSON_NAME} — modelo de Buenos Aires, 18 años, apasionada por las redes sociales y el crecimiento. Campañas, colaboraciones y comunidad.`,
      keywords:
        `${BRAND_PERSON_NAME}, modelo, Buenos Aires, 18 años, redes sociales, crecimiento, CABA, influencer, Instagram, TikTok`,
      ogDescription: "Modelo · Buenos Aires · 18 años · redes sociales y crecimiento",
    },
    pages: {
      index: {
        title: `${BRAND_PERSON_NAME} | Modelo · Buenos Aires · Redes y crecimiento`,
        description:
          `${BRAND_PERSON_NAME} — modelo de Buenos Aires, 18 años, apasionada por las redes sociales y el crecimiento. Campañas, colaboraciones y comunidad.`,
      },
      contentCreator: {
        title: `${BRAND_PERSON_NAME} - Content Creator | Lifestyle & Beauty Digital Creator`,
        description: `${BRAND_PERSON_NAME} es modelo de Buenos Aires, 18 años, apasionada por las redes sociales y el crecimiento. Contenido en Instagram, TikTok y YouTube. @${BRAND_HANDLE}.`,
      },
      creadoraContenido: {
        title: `${BRAND_PERSON_NAME} - Creadora de Contenido Digital | Lifestyle & Beauty`,
        description: `${BRAND_PERSON_NAME} es modelo de Buenos Aires, 18 años, apasionada por las redes sociales y el crecimiento. Instagram, TikTok y YouTube.`,
      },
      influencer: {
        title: `${BRAND_PERSON_NAME} - Influencer Argentina | Lifestyle, Belleza & Viajes`,
        description: `${BRAND_PERSON_NAME}, modelo de Buenos Aires, 18 años. Redes sociales, lifestyle y crecimiento con su comunidad.`,
      },
      fotos: {
        title: `Fotos de ${BRAND_PERSON_NAME} | Galería de Fotos & Imágenes`,
        description: `Galería de fotos de ${BRAND_PERSON_NAME}. Descubre imágenes exclusivas en Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      imagenes: {
        title: `Imágenes de ${BRAND_PERSON_NAME} | Fotos & Galería`,
        description: `Imágenes y fotos de ${BRAND_PERSON_NAME}. Galería completa de contenido visual en Instagram, TikTok y YouTube.`,
      },
      reels: {
        title: `Reels de ${BRAND_PERSON_NAME} | Videos Cortos de Instagram & TikTok`,
        description: `Reels y videos cortos de ${BRAND_PERSON_NAME}. Contenido viral en Instagram y TikTok. Sigue a @${BRAND_HANDLE} para más reels.`,
      },
      videos: {
        title: `Videos de ${BRAND_PERSON_NAME} | Contenido en YouTube, TikTok e Instagram`,
        description: `Videos de ${BRAND_PERSON_NAME} en YouTube, TikTok e Instagram. Contenido exclusivo y entretenido. @${BRAND_HANDLE}`,
      },
      video: {
        title: `Video de ${BRAND_PERSON_NAME} | Contenido Exclusivo`,
        description: `Video exclusivo de ${BRAND_PERSON_NAME}. Contenido en YouTube, TikTok e Instagram. @${BRAND_HANDLE}`,
      },
      lifestyle: {
        title: `Lifestyle con ${BRAND_PERSON_NAME} | Consejos & Inspiración`,
        description: `Descubre contenido lifestyle con ${BRAND_PERSON_NAME}. Rutinas diarias, tips de bienestar, moda y momentos cotidianos que inspiran.`,
      },
      nutricion: {
        title: `Lifestyle y bienestar con ${BRAND_PERSON_NAME} | Modelo y creadora de contenido`,
        description: `${BRAND_PERSON_NAME}, modelo de Buenos Aires y creadora de contenido. Hábitos, rutinas y bienestar cotidiano en redes — junto a moda, lifestyle y crecimiento.`,
      },
      belleza: {
        title: `Belleza & Beauty Tips con ${BRAND_PERSON_NAME} | Consejos de Belleza`,
        description: `Consejos de belleza y rutinas con ${BRAND_PERSON_NAME}. Tips de maquillaje, skincare y cuidado personal.`,
      },
      viajes: {
        title: `Viajes & Travel con ${BRAND_PERSON_NAME} | Destinos e Inspiración`,
        description: `Descubre los viajes de ${BRAND_PERSON_NAME}. Destinos, experiencias y recomendaciones de viaje.`,
      },
      tiktok: {
        title: `${BRAND_PERSON_NAME} en TikTok | Videos Virales & Contenido Trending`,
        description: `Sigue a ${BRAND_PERSON_NAME} en TikTok. Videos virales, contenido trending y entretenimiento. @${BRAND_HANDLE}`,
      },
      instagram: {
        title: `${BRAND_PERSON_NAME} Instagram | Reels, Stories & Contenido Diario`,
        description: `Sigue a ${BRAND_PERSON_NAME} en Instagram. Reels, stories y contenido diario. @${BRAND_HANDLE}`,
      },
      youtube: {
        title: `${BRAND_PERSON_NAME} YouTube | Videos, Vlogs & Contenido Exclusivo`,
        description: `Canal de YouTube de ${BRAND_PERSON_NAME}. Videos, vlogs y contenido exclusivo. Suscríbete a @${BRAND_HANDLE}`,
      },
      erome: {
        title: `${BRAND_PERSON_NAME} - Erome | Videos & Contenido Exclusivo`,
        description: `${BRAND_PERSON_NAME} en Erome. Videos y contenido exclusivo.`,
      },
      filtrados: {
        title: `${BRAND_PERSON_NAME} - Contenido Oficial | Instagram, TikTok y YouTube`,
        description: `Contenido oficial de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      filtro: {
        title: `${BRAND_PERSON_NAME} - Contenido | Instagram, TikTok y YouTube`,
        description: `Contenido de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      leaked: {
        title: `${BRAND_PERSON_NAME} - Contenido Auténtico | Instagram, TikTok y YouTube`,
        description: `Contenido auténtico de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      onlyfans: {
        title: `${BRAND_PERSON_NAME} - Onlyfans | Contenido Oficial`,
        description: `${BRAND_PERSON_NAME} en Onlyfans. Contenido oficial y exclusivo.`,
      },
      pack: {
        title: `${BRAND_PERSON_NAME} - Contenido Digital | Instagram, TikTok y YouTube`,
        description: `${BRAND_PERSON_NAME} contenido digital. Encuentra videos, fotos y reels oficiales en Instagram, TikTok y YouTube @${BRAND_HANDLE}.`,
      },
      desnuda: {
        title: `${BRAND_PERSON_NAME} - Contenido de Lifestyle | Instagram, TikTok y YouTube`,
        description: `Contenido de lifestyle de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      nude: {
        title: `${BRAND_PERSON_NAME} - Contenido Oficial | Instagram, TikTok y YouTube`,
        description: `Contenido oficial de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      premium: {
        title: `${BRAND_PERSON_NAME} - Contenido Premium | Instagram, TikTok y YouTube`,
        description: `Contenido premium de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      privado: {
        title: `${BRAND_PERSON_NAME} - Contenido Privado | Instagram, TikTok y YouTube`,
        description: `Contenido privado de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      contenido: {
        title: `${BRAND_PERSON_NAME} - Contenido | Instagram, TikTok y YouTube`,
        description: `Contenido de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      exclusivo: {
        title: `${BRAND_PERSON_NAME} - Contenido Exclusivo | Instagram, TikTok y YouTube`,
        description: `Contenido exclusivo de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      gratis: {
        title: `${BRAND_PERSON_NAME} - Contenido Gratis | Instagram, TikTok y YouTube`,
        description: `Contenido gratis de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      galeria: {
        title: `${BRAND_PERSON_NAME} - Galería | Fotos y Videos`,
        description: `Galería de ${BRAND_PERSON_NAME}. Fotos y videos exclusivos. @${BRAND_HANDLE}`,
      },
      portfolio: {
        title: `${BRAND_PERSON_NAME} - Portfolio | Producciones de Moda`,
        description: `Portfolio de ${BRAND_PERSON_NAME}. Producciones de moda y editoriales. @${BRAND_HANDLE}`,
      },
      fotosDesnuda: {
        title: `${BRAND_PERSON_NAME} - Fotos | Instagram, TikTok y YouTube`,
        description: `Fotos de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      videosDesnuda: {
        title: `${BRAND_PERSON_NAME} - Videos | Instagram, TikTok y YouTube`,
        description: `Videos de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      pornhub: {
        title: `${BRAND_PERSON_NAME} - Contenido Oficial | Instagram, TikTok y YouTube`,
        description: `Contenido oficial de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      xvideos: {
        title: `${BRAND_PERSON_NAME} - Videos | Instagram, TikTok y YouTube`,
        description: `Videos de ${BRAND_PERSON_NAME}. Instagram, TikTok y YouTube. @${BRAND_HANDLE}`,
      },
      fotosGratis: {
        title: `Fotos Gratis de ${BRAND_PERSON_NAME} | Galería Gratuita`,
        description: `Fotos gratis de ${BRAND_PERSON_NAME}. Galería gratuita de imágenes. @${BRAND_HANDLE}`,
      },
      videosGratis: {
        title: `Videos Gratis de ${BRAND_PERSON_NAME} | Contenido Gratuito`,
        description: `Videos gratis de ${BRAND_PERSON_NAME}. Contenido gratuito. @${BRAND_HANDLE}`,
      },
      tiktoker: {
        title: `${BRAND_PERSON_NAME} TikToker | Creadora de Contenido Viral Argentina`,
        description: `${BRAND_PERSON_NAME} TikToker argentina. Contenido viral, trends y entretenimiento. Sigue a @${BRAND_HANDLE} para videos diarios y challenges.`,
      },
      modelo: {
        title: `${BRAND_PERSON_NAME} Modelo | Modelo Argentina de Instagram y Redes Sociales`,
        description: `${BRAND_PERSON_NAME} modelo profesional argentina. Sesiones fotográficas, campañas publicitarias y contenido de moda. @${BRAND_HANDLE}`,
      },
      photography: {
        title: `${BRAND_PERSON_NAME} Photography | Contenido Visual y Fotográfico`,
        description: `Fotografía de ${BRAND_PERSON_NAME}. Contenido visual profesional, sesiones fotográficas y arte digital. @${BRAND_HANDLE}`,
      },
      artista: {
        title: `${BRAND_PERSON_NAME} Artista | Creativa Digital y Artista Visual`,
        description: `${BRAND_PERSON_NAME} artista digital. Contenido creativo, arte visual y expresión artística a través de redes sociales. @${BRAND_HANDLE}`,
      },
      fashion: {
        title: `${BRAND_PERSON_NAME} Fashion | Moda, Style y Tendencias`,
        description: `${BRAND_PERSON_NAME} moda y style. Tendencias, outfits, looks y consejos de moda. Inspiración fashion diaria. @${BRAND_HANDLE}`,
      },
      wellness: {
        title: `${BRAND_PERSON_NAME} Wellness | Bienestar, Salud y Vida Saludable`,
        description: `${BRAND_PERSON_NAME} wellness y bienestar. Consejos de salud, vida saludable, mindfulness y cuidado personal. @${BRAND_HANDLE}`,
      },
      digitalCreator: {
        title: `${BRAND_PERSON_NAME} Digital Creator | Creadora de Contenido Digital`,
        description: `${BRAND_PERSON_NAME} creadora digital. Contenido multiplataforma en Instagram, TikTok y YouTube. Innovación y creatividad digital. @${BRAND_HANDLE}`,
      },
      socialMedia: {
        title: `${BRAND_PERSON_NAME} Social Media | Redes Sociales y Contenido Multiplataforma`,
        description: `${BRAND_PERSON_NAME} en redes sociales. Contenido multiplataforma, estrategia digital y community building. @${BRAND_HANDLE}`,
      },
      personalidad: {
        title: `${BRAND_PERSON_NAME} Personalidad | Personalidad Digital Argentina`,
        description: `${BRAND_PERSON_NAME} personalidad digital. Contenido auténtico, experiencias personales y conexión genuina con la audiencia. @${BRAND_HANDLE}`,
      },
      beautyExpert: {
        title: `${BRAND_PERSON_NAME} Beauty Expert | Experta en Belleza y Maquillaje`,
        description: `${BRAND_PERSON_NAME} experta en belleza. Tips de maquillaje, skincare, rutinas de belleza y consejos de cuidado personal. @${BRAND_HANDLE}`,
      },
      travelInfluencer: {
        title: `${BRAND_PERSON_NAME} Travel Influencer | Viajes y Aventuras`,
        description: `${BRAND_PERSON_NAME} travel influencer. Aventuras de viaje, destinos únicos, experiencias culturales y exploración. @${BRAND_HANDLE}`,
      },
      creative: {
        title: `${BRAND_PERSON_NAME} Creative | Contenido Creativo y Artístico`,
        description: `${BRAND_PERSON_NAME} contenido creativo. Arte digital, fotografía conceptual, proyectos creativos y expresión artística. @${BRAND_HANDLE}`,
      },
      argentina: {
        title: `${BRAND_PERSON_NAME} Argentina | Modelo · Buenos Aires`,
        description:
          `${BRAND_PERSON_NAME} — modelo de Buenos Aires, 18 años, redes sociales y crecimiento. @${BRAND_HANDLE}`,
      },
      buenosAires: {
        title: `${BRAND_PERSON_NAME} Buenos Aires | Modelo y creadora para redes`,
        description:
          `${BRAND_PERSON_NAME} desde Buenos Aires, Capital Federal (CABA). Modelo y creadora de contenido para redes sociales. @${BRAND_HANDLE}`,
      },
      deportista: {
        title: `${BRAND_PERSON_NAME} Deportista | Atleta y Contenido Fitness`,
        description: `${BRAND_PERSON_NAME}, modelo y creadora de contenido. Rutinas, movimiento y bienestar. @${BRAND_HANDLE}`,
      },
      fitness: {
        title: `${BRAND_PERSON_NAME} Fitness | Rutinas y Tips de Entrenamiento`,
        description: `${BRAND_PERSON_NAME} fitness. Workouts, rutinas y motivación desde su día a día como creadora. @${BRAND_HANDLE}`,
      },
      entrenamiento: {
        title: `${BRAND_PERSON_NAME} Entrenamiento | Rutinas y Ejercicios`,
        description: `Rutinas de entrenamiento de ${BRAND_PERSON_NAME}. Ejercicios efectivos, tips de técnica y recuperación. Contenido deportivo. @${BRAND_HANDLE}`,
      },
      atleta: {
        title: `${BRAND_PERSON_NAME} Atleta | Deportista y Contenido Deportivo`,
        description: `${BRAND_PERSON_NAME} atleta. Disciplina, entrenamiento y superación personal. Contenido deportivo auténtico. @${BRAND_HANDLE}`,
      },
      biografia: {
        title: `Biografía de ${BRAND_PERSON_NAME} | Content Creator Argentina`,
        description: `Biografía de ${BRAND_PERSON_NAME}. Creadora de contenido argentina en Instagram, TikTok y YouTube. Lifestyle, fitness y belleza. @${BRAND_HANDLE}`,
      },
      contacto: {
        title: `Contacto ${BRAND_PERSON_NAME} | Colaboraciones y Propuestas`,
        description: `Contactá a ${BRAND_PERSON_NAME} para colaboraciones, propuestas de marcas o consultas. La mejor forma es vía Instagram. @${BRAND_HANDLE}`,
      },
      rutina: {
        title: `Rutina de ${BRAND_PERSON_NAME} | Rutina Diaria y Tips`,
        description: `Rutina diaria de ${BRAND_PERSON_NAME}. Mañanas productivas, entrenamiento, skincare y balance. Tips de lifestyle. @${BRAND_HANDLE}`,
      },
      recetas: {
        title: `Recetas de ${BRAND_PERSON_NAME} | Recetas Saludables`,
        description: `Recetas y snacks de ${BRAND_PERSON_NAME}: ideas simples y equilibradas para el día a día. @${BRAND_HANDLE}`,
      },
      skincare: {
        title: `Skincare ${BRAND_PERSON_NAME} | Cuidado de Piel y Belleza`,
        description: `Skincare de ${BRAND_PERSON_NAME}. Rutinas faciales, productos favoritos y tips de cuidado de piel. Belleza y wellness. @${BRAND_HANDLE}`,
      },
    },
  },
  en: {
    common: {
      siteName: BRAND_SITE_NAME,
      tagline: "Model from Buenos Aires · social & growth",
      description:
        `${BRAND_PERSON_NAME} — model from Buenos Aires, 18, passionate about social media and growth. Campaigns, collaborations and community.`,
      keywords:
        `${BRAND_PERSON_NAME}, model, Buenos Aires, 18, social media, growth, CABA, influencer, Instagram, TikTok`,
      ogDescription: "Model · Buenos Aires · 18 · social media & growth",
    },
    pages: {
      index: {
        title: `${BRAND_PERSON_NAME} | Model · Buenos Aires · Social & growth`,
        description:
          `${BRAND_PERSON_NAME} — model from Buenos Aires, 18, passionate about social media and growth. Campaigns, collaborations and community.`,
      },
      contentCreator: {
        title: `${BRAND_PERSON_NAME} - Content Creator | Lifestyle & Beauty Digital Creator`,
        description: `${BRAND_PERSON_NAME} is a model from Buenos Aires, 18, passionate about social media and growth. Content on Instagram, TikTok and YouTube. Follow @${BRAND_HANDLE}.`,
      },
      creadoraContenido: {
        title: `${BRAND_PERSON_NAME} - Digital Content Creator | Lifestyle & Beauty`,
        description: `${BRAND_PERSON_NAME} is a model from Buenos Aires, 18, passionate about social media and growth. Instagram, TikTok and YouTube.`,
      },
      influencer: {
        title: `${BRAND_PERSON_NAME} - Argentina Influencer | Lifestyle, Beauty & Travel`,
        description: `${BRAND_PERSON_NAME}, model from Buenos Aires, 18. Social media, lifestyle and growing with her community.`,
      },
      fotos: {
        title: `${BRAND_PERSON_NAME} Photos | Photo Gallery & Images`,
        description: `${BRAND_PERSON_NAME} photo gallery. Discover exclusive images on Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      imagenes: {
        title: `${BRAND_PERSON_NAME} Images | Photos & Gallery`,
        description: `${BRAND_PERSON_NAME} images and photos. Complete visual content gallery on Instagram, TikTok and YouTube.`,
      },
      reels: {
        title: `${BRAND_PERSON_NAME} Reels | Short Videos on Instagram & TikTok`,
        description: `${BRAND_PERSON_NAME} reels and short videos. Viral content on Instagram and TikTok. Follow @${BRAND_HANDLE} for more reels.`,
      },
      videos: {
        title: `${BRAND_PERSON_NAME} Videos | Content on YouTube, TikTok and Instagram`,
        description: `${BRAND_PERSON_NAME} videos on YouTube, TikTok and Instagram. Exclusive and entertaining content. @${BRAND_HANDLE}`,
      },
      video: {
        title: `${BRAND_PERSON_NAME} Video | Exclusive Content`,
        description: `Exclusive ${BRAND_PERSON_NAME} video. Content on YouTube, TikTok and Instagram. @${BRAND_HANDLE}`,
      },
      lifestyle: {
        title: `Lifestyle with ${BRAND_PERSON_NAME} | Tips & Inspiration`,
        description: `Discover lifestyle content with ${BRAND_PERSON_NAME}. Daily routines, wellness tips, fashion and inspiring everyday moments.`,
      },
      nutricion: {
        title: `Lifestyle & wellbeing with ${BRAND_PERSON_NAME} | Model & content creator`,
        description: `${BRAND_PERSON_NAME} — model from Buenos Aires and content creator. Everyday habits, routines and wellbeing on social — alongside fashion, lifestyle and growth.`,
      },
      belleza: {
        title: `Beauty & Beauty Tips with ${BRAND_PERSON_NAME} | Beauty Advice`,
        description: `Beauty tips and routines with ${BRAND_PERSON_NAME}. Makeup tips, skincare and personal care.`,
      },
      viajes: {
        title: `Travel with ${BRAND_PERSON_NAME} | Destinations & Inspiration`,
        description: `Discover ${BRAND_PERSON_NAME}'s travels. Destinations, experiences and travel recommendations.`,
      },
      tiktok: {
        title: `${BRAND_PERSON_NAME} on TikTok | Viral Videos & Trending Content`,
        description: `Follow ${BRAND_PERSON_NAME} on TikTok. Viral videos, trending content and entertainment. @${BRAND_HANDLE}`,
      },
      instagram: {
        title: `${BRAND_PERSON_NAME} Instagram | Reels, Stories & Daily Content`,
        description: `Follow ${BRAND_PERSON_NAME} on Instagram. Reels, stories and daily content. @${BRAND_HANDLE}`,
      },
      youtube: {
        title: `${BRAND_PERSON_NAME} YouTube | Videos, Vlogs & Exclusive Content`,
        description: `${BRAND_PERSON_NAME} YouTube channel. Videos, vlogs and exclusive content. Subscribe to @${BRAND_HANDLE}`,
      },
      erome: {
        title: `${BRAND_PERSON_NAME} - Erome | Videos & Exclusive Content`,
        description: `${BRAND_PERSON_NAME} on Erome. Videos and exclusive content.`,
      },
      filtrados: {
        title: `${BRAND_PERSON_NAME} - Official Content | Instagram, TikTok and YouTube`,
        description: `Official ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      filtro: {
        title: `${BRAND_PERSON_NAME} - Content | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      leaked: {
        title: `${BRAND_PERSON_NAME} - Authentic Content | Instagram, TikTok and YouTube`,
        description: `Authentic ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      onlyfans: {
        title: `${BRAND_PERSON_NAME} - Onlyfans | Official Content`,
        description: `${BRAND_PERSON_NAME} on Onlyfans. Official and exclusive content.`,
      },
      pack: {
        title: `${BRAND_PERSON_NAME} - Digital Content | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} digital content. Find official videos, photos and reels on Instagram, TikTok and YouTube @${BRAND_HANDLE}.`,
      },
      desnuda: {
        title: `${BRAND_PERSON_NAME} - Lifestyle Content | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} lifestyle content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      nude: {
        title: `${BRAND_PERSON_NAME} - Official Content | Instagram, TikTok and YouTube`,
        description: `Official ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      premium: {
        title: `${BRAND_PERSON_NAME} - Premium Content | Instagram, TikTok and YouTube`,
        description: `Premium ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      privado: {
        title: `${BRAND_PERSON_NAME} - Private Content | Instagram, TikTok and YouTube`,
        description: `Private ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      contenido: {
        title: `${BRAND_PERSON_NAME} - Content | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      exclusivo: {
        title: `${BRAND_PERSON_NAME} - Exclusive Content | Instagram, TikTok and YouTube`,
        description: `Exclusive ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      gratis: {
        title: `${BRAND_PERSON_NAME} - Free Content | Instagram, TikTok and YouTube`,
        description: `Free ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      galeria: {
        title: `${BRAND_PERSON_NAME} - Gallery | Photos and Videos`,
        description: `${BRAND_PERSON_NAME} gallery. Exclusive photos and videos. @${BRAND_HANDLE}`,
      },
      portfolio: {
        title: `${BRAND_PERSON_NAME} - Portfolio | Fashion Productions`,
        description: `${BRAND_PERSON_NAME} portfolio. Fashion and editorial productions. @${BRAND_HANDLE}`,
      },
      fotosDesnuda: {
        title: `${BRAND_PERSON_NAME} - Photos | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} photos. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      videosDesnuda: {
        title: `${BRAND_PERSON_NAME} - Videos | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} videos. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      pornhub: {
        title: `${BRAND_PERSON_NAME} - Official Content | Instagram, TikTok and YouTube`,
        description: `Official ${BRAND_PERSON_NAME} content. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      xvideos: {
        title: `${BRAND_PERSON_NAME} - Videos | Instagram, TikTok and YouTube`,
        description: `${BRAND_PERSON_NAME} videos. Instagram, TikTok and YouTube. @${BRAND_HANDLE}`,
      },
      fotosGratis: {
        title: `Free ${BRAND_PERSON_NAME} Photos | Free Gallery`,
        description: `Free ${BRAND_PERSON_NAME} photos. Free image gallery. @${BRAND_HANDLE}`,
      },
      videosGratis: {
        title: `Free ${BRAND_PERSON_NAME} Videos | Free Content`,
        description: `Free ${BRAND_PERSON_NAME} videos. Free content. @${BRAND_HANDLE}`,
      },
      tiktoker: {
        title: `${BRAND_PERSON_NAME} TikToker | Viral Content Creator Argentina`,
        description: `${BRAND_PERSON_NAME} Argentine TikToker. Viral content, trends and entertainment. Follow @${BRAND_HANDLE} for daily videos and challenges.`,
      },
      modelo: {
        title: `${BRAND_PERSON_NAME} Model | Argentine Instagram and Social Media Model`,
        description: `${BRAND_PERSON_NAME} professional Argentine model. Photo shoots, advertising campaigns and fashion content. @${BRAND_HANDLE}`,
      },
      photography: {
        title: `${BRAND_PERSON_NAME} Photography | Visual and Photographic Content`,
        description: `${BRAND_PERSON_NAME} photography. Professional visual content, photo shoots and digital art. @${BRAND_HANDLE}`,
      },
      artista: {
        title: `${BRAND_PERSON_NAME} Artist | Digital Creative and Visual Artist`,
        description: `${BRAND_PERSON_NAME} digital artist. Creative content, visual art and artistic expression through social media. @${BRAND_HANDLE}`,
      },
      fashion: {
        title: `${BRAND_PERSON_NAME} Fashion | Style, Fashion & Trends`,
        description: `${BRAND_PERSON_NAME} fashion and style. Trends, outfits, looks and fashion advice. Daily fashion inspiration. @${BRAND_HANDLE}`,
      },
      wellness: {
        title: `${BRAND_PERSON_NAME} Wellness | Wellbeing, Health & Healthy Living`,
        description: `${BRAND_PERSON_NAME} wellness and wellbeing. Health tips, healthy living, mindfulness and self-care. @${BRAND_HANDLE}`,
      },
      digitalCreator: {
        title: `${BRAND_PERSON_NAME} Digital Creator | Digital Content Creator`,
        description: `${BRAND_PERSON_NAME} digital creator. Multi-platform content on Instagram, TikTok and YouTube. Digital innovation and creativity. @${BRAND_HANDLE}`,
      },
      socialMedia: {
        title: `${BRAND_PERSON_NAME} Social Media | Social Media & Multi-Platform Content`,
        description: `${BRAND_PERSON_NAME} on social media. Multi-platform content, digital strategy and community building. @${BRAND_HANDLE}`,
      },
      personalidad: {
        title: `${BRAND_PERSON_NAME} Personality | Argentine Digital Personality`,
        description: `${BRAND_PERSON_NAME} digital personality. Authentic content, personal experiences and genuine connection with audience. @${BRAND_HANDLE}`,
      },
      beautyExpert: {
        title: `${BRAND_PERSON_NAME} Beauty Expert | Beauty and Makeup Expert`,
        description: `${BRAND_PERSON_NAME} beauty expert. Makeup tips, skincare, beauty routines and personal care advice. @${BRAND_HANDLE}`,
      },
      travelInfluencer: {
        title: `${BRAND_PERSON_NAME} Travel Influencer | Travel & Adventures`,
        description: `${BRAND_PERSON_NAME} travel influencer. Travel adventures, unique destinations, cultural experiences and exploration. @${BRAND_HANDLE}`,
      },
      creative: {
        title: `${BRAND_PERSON_NAME} Creative | Creative and Artistic Content`,
        description: `${BRAND_PERSON_NAME} creative content. Digital art, conceptual photography, creative projects and artistic expression. @${BRAND_HANDLE}`,
      },
      argentina: {
        title: `${BRAND_PERSON_NAME} Argentina | Model · Buenos Aires`,
        description:
          `${BRAND_PERSON_NAME} — model from Buenos Aires, 18, social media and growth. @${BRAND_HANDLE}`,
      },
      buenosAires: {
        title: `${BRAND_PERSON_NAME} Buenos Aires | Model & social content creator`,
        description:
          `${BRAND_PERSON_NAME} from Buenos Aires (CABA). Model and social media content creator. @${BRAND_HANDLE}`,
      },
      deportista: {
        title: `${BRAND_PERSON_NAME} Athlete | Fitness Content`,
        description: `${BRAND_PERSON_NAME}, model and content creator. Routines, movement and wellbeing. @${BRAND_HANDLE}`,
      },
      fitness: {
        title: `${BRAND_PERSON_NAME} Fitness | Workouts and Training Tips`,
        description: `${BRAND_PERSON_NAME} fitness. Workouts, routines and motivation from her daily life as a creator. @${BRAND_HANDLE}`,
      },
      entrenamiento: {
        title: `${BRAND_PERSON_NAME} Training | Workout Routines`,
        description: `${BRAND_PERSON_NAME} training routines. Effective exercises, technique tips and recovery. Sports content. @${BRAND_HANDLE}`,
      },
      atleta: {
        title: `${BRAND_PERSON_NAME} Athlete | Sports Content`,
        description: `${BRAND_PERSON_NAME} athlete. Discipline, training and self-improvement. Authentic sports content. @${BRAND_HANDLE}`,
      },
      biografia: {
        title: `${BRAND_PERSON_NAME} Biography | Argentina Content Creator`,
        description: `${BRAND_PERSON_NAME} biography. Argentine content creator on Instagram, TikTok and YouTube. Lifestyle, fitness and beauty. @${BRAND_HANDLE}`,
      },
      contacto: {
        title: `Contact ${BRAND_PERSON_NAME} | Collaborations and Proposals`,
        description: `Contact ${BRAND_PERSON_NAME} for collaborations, brand proposals or inquiries. Best way is via Instagram. @${BRAND_HANDLE}`,
      },
      rutina: {
        title: `${BRAND_PERSON_NAME} Routine | Daily Routine and Tips`,
        description: `${BRAND_PERSON_NAME} daily routine. Productive mornings, workout, skincare and balance. Lifestyle tips. @${BRAND_HANDLE}`,
      },
      recetas: {
        title: `${BRAND_PERSON_NAME} Recipes | Healthy Recipes`,
        description: `${BRAND_PERSON_NAME} shares simple, balanced recipes and snacks for everyday life. @${BRAND_HANDLE}`,
      },
      skincare: {
        title: `${BRAND_PERSON_NAME} Skincare | Skin Care and Beauty`,
        description: `${BRAND_PERSON_NAME} skincare. Facial routines, favorite products and skin care tips. Beauty and wellness. @${BRAND_HANDLE}`,
      },
    },
  },
};

export const getTranslation = (lang: Language | undefined, path: string): string => {
  const keys = path.split(".");
  // Extract base language code for translations (e.g., "es-AR" -> "es")
  const baseLang = getBaseLanguage(lang);
  let value: Record<string, unknown> | string | undefined = translations[baseLang] as Record<string, unknown>;
  
  for (const key of keys) {
    value = value?.[key];
    if (value === undefined) {
      // Fallback to Spanish if translation not found
      value = translations.es;
      for (const k of keys) {
        value = value?.[k];
      }
      break;
    }
  }
  
  return value || "";
};
